import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../../useTranslation';

// Pagination component
export const Pagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPrevPage: () => void;
    onNextPage: () => void;
}> = ({ currentPage, totalPages, onPageChange, onPrevPage, onNextPage }) => {
    // Get translations
    const { t } = useTranslation(localStorage.getItem('travelDiarySettings') ?
        JSON.parse(localStorage.getItem('travelDiarySettings')!).language : 'ru-RU');
    
    // Generate page numbers to display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5; // Show at most 5 page numbers
        
        if (totalPages <= maxPagesToShow) {
            // If we have 5 or fewer pages, show all of them
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);
            
            // Calculate start and end of page numbers to show
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            
            // Adjust if we're at the beginning or end
            if (currentPage <= 2) {
                end = 4;
            } else if (currentPage >= totalPages - 1) {
                start = totalPages - 3;
            }
            
            // Add ellipsis if needed
            if (start > 2) {
                pageNumbers.push('...');
            }
            
            // Add middle pages
            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }
            
            // Add ellipsis if needed
            if (end < totalPages - 1) {
                pageNumbers.push('...');
            }
            
            // Always show last page
            pageNumbers.push(totalPages);
        }
        
        return pageNumbers;
    };
    
    // Don't render pagination if there's only one page
    if (totalPages <= 1) return null;
    
    return (
        <div className="flex justify-center items-center mt-6 mb-8">
            <nav className="flex items-center space-x-1">
                {/* Previous page button */}
                <button 
                    onClick={onPrevPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${
                        currentPage === 1 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    aria-label={t.previousPage || "Previous page"}
                >
                    <ChevronLeft size={20} />
                </button>
                
                {/* Page numbers */}
                {getPageNumbers().map((page, index) => (
                    <React.Fragment key={index}>
                        {page === '...' ? (
                            <span className="px-3 py-2 text-gray-500 dark:text-gray-400">...</span>
                        ) : (
                            <button
                                onClick={() => typeof page === 'number' && onPageChange(page)}
                                className={`px-3 py-1 rounded-md ${
                                    currentPage === page
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                                }`}
                            >
                                {page}
                            </button>
                        )}
                    </React.Fragment>
                ))}
                
                {/* Next page button */}
                <button 
                    onClick={onNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${
                        currentPage === totalPages 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    aria-label={t.nextPage || "Next page"}
                >
                    <ChevronRight size={20} />
                </button>
            </nav>
        </div>
    );
};